import type {
  AnySpec,
  KindMap,
  UnitValues
} from 'v5/core/form-schema/formKinds.types';
import { type ListItemValues, rowIdUtils } from 'v5/core/form-schema/formRows';
import { fieldPath } from './unitPath';

/**
 * One list row inside a react-hook-form form: its row id as a key string,
 * its values under path-safe field keys (`core:rates` → `core_rates`), the
 * keys its fields write to.
 */
export type ListFormRow<M extends KindMap<M>> = {
  rowKey: string;
  values: UnitValues<M>;
};

/** Field values keyed by path-safe keys, as the fields of a form read them. */
export function toValuesShape<M extends KindMap<M>>(
  values: UnitValues<M>
): UnitValues<M> {
  const out: UnitValues<M> = {};
  for (const [key, value] of Object.entries(values)) {
    out[fieldPath('', key)] = value;
  }
  return out;
}

/** A row's values back under its fields' keys. */
export function readRowValues<M extends KindMap<M>>(
  fields: ReadonlyArray<AnySpec<M>>,
  values: UnitValues<M>
): UnitValues<M> {
  const out: UnitValues<M> = {};
  for (const field of fields) {
    out[field.key] = values[fieldPath('', field.key)];
  }
  return out;
}

export function toListFormRows<M extends KindMap<M>>(
  items: ReadonlyArray<ListItemValues<M>>
): ListFormRow<M>[] {
  return items.map(item => ({
    rowKey: rowIdUtils.key(item.rowId),
    values: toValuesShape(item.values)
  }));
}

/**
 * The rows of a list form as list items, in their order: a row that was
 * saved keeps its id (so the write updates it), a row added meanwhile gets a
 * local one.
 */
export function fromListFormRows<M extends KindMap<M>>(
  fields: ReadonlyArray<AnySpec<M>>,
  rows: ReadonlyArray<ListFormRow<M>>,
  saved: ReadonlyArray<ListItemValues<M>>
): ListItemValues<M>[] {
  return rows.map(row => ({
    rowId:
      saved.find(item => rowIdUtils.key(item.rowId) === row.rowKey)?.rowId ??
      rowIdUtils.local(),
    values: readRowValues(fields, row.values)
  }));
}

/** A blank or template row to append (`newItem` of the list's binding). */
export function newListFormRow<M extends KindMap<M>>(
  values: UnitValues<M>
): ListFormRow<M> {
  return {
    rowKey: rowIdUtils.key(rowIdUtils.local()),
    values: toValuesShape(values)
  };
}
